import { faCheck, faTimes } from '@fortawesome/pro-regular-svg-icons'
import {
  Box,
  Flex,
  Loader,
  Text,
  TextInput,
} from '@mantine/core'
import { useDebouncedState } from '@mantine/hooks'
import { useCallback, useEffect, useState } from 'react'

import { getStoreByDomain } from '@flecto/api/store'
import { useQueryGetStoreDetails } from '@flecto/core/saas'
import { Trans, useTranslation } from '@flecto/translations'
import { Icon, CollapsibleCard } from '@flecto/ui'

import { FormImages } from '../FormStoreSettings/FormImages'
import { FormSection } from '../FormStoreSettings/FormSection'
import { useFormContext } from '../FormStoreSettings/FormStoreSettings'
import styles from './styles.module.css'

export function CardStoreUrl() {
  const { t } = useTranslation('saas', { keyPrefix: 'profile_store' })

  const [isLoading, setIsLoading] = useState(false)
  const [isDomainAvailable, setIsDomainAvailable] = useState<boolean | null>(
    null
  )

  const form = useFormContext()

  const { data: store } = useQueryGetStoreDetails()

  const [value, setValue] = useDebouncedState(form.values.domain, 500)

  const handleCheckDomain = useCallback(async () => {
    if (
      !form.isValid('domain') ||
      !form.isDirty('domain') ||
      value === store?.domain
    ) {
      setIsDomainAvailable(null)
      return
    }

    try {
      setIsLoading(true)
      await getStoreByDomain(value ?? '')
      setIsDomainAvailable(false)
    } catch {
      setIsDomainAvailable(true)
    } finally {
      setIsLoading(false)
    }
  }, [value])

  useEffect(() => {
    if (form.values.domain === value) {
      return
    }

    form.setFieldValue('domain', value)

    if (!value || value.length <= 1) {
      setIsDomainAvailable(null)
      setIsLoading(false)
      return
    }

    handleCheckDomain()
  }, [value])

  useEffect(() => {
    if (form.values.domain !== value) {
      setValue(form.values.domain)
    }
  }, [form.values])

  return (
    <FormSection>
      <CollapsibleCard opened>
        <CollapsibleCard.Title>{t('section_url.title')}</CollapsibleCard.Title>
        <CollapsibleCard.Description>
          <Box mr='sm'>{t('section_url.description')}</Box>
        </CollapsibleCard.Description>
        <CollapsibleCard.Content>
          <TextInput
            mt='sm'
            key={form.key('domain')}
            className={styles.inputDomain}
            placeholder={t('section_url.url_placeholder')}
            leftSection='https://'
            rightSection={
              <Text w={100} mr='sm'>
                .flecto.store
              </Text>
            }
            defaultValue={value}
            onChange={(e) => setValue(e.currentTarget.value)}
            error={form.errors.domain}
            withAsterisk
          />
          {isLoading ? (
            <Flex align='center' gap='xs' mt='sm'>
              <Loader size={10} />
              <Text size='sm'>{t('section_url.domain_checking')}</Text>
            </Flex>
          ) : isDomainAvailable === true ? (
            <Flex align='center' gap='xs' mt='sm'>
              <Icon icon={faCheck} color='green' />
              <Text size='sm' c='green'>
                <Trans
                  ns='saas'
                  i18nKey='profile_store.section_url.domain_available'
                  values={{ domain: value }}
                  components={[<b key={1} />]}
                />
              </Text>
            </Flex>
          ) : isDomainAvailable === false ? (
            <Flex align='center' gap='xs' mt='sm'>
              <Icon icon={faTimes} color='red' />
              <Text size='sm' c='red'>
                <Trans
                  ns='saas'
                  i18nKey='profile_store.section_url.domain_unavailable'
                  values={{ domain: value }}
                  components={[<b key={1} />]}
                />
              </Text>
            </Flex>
          ) : null}
        </CollapsibleCard.Content>
      </CollapsibleCard>
      <FormImages images={[require('./image.jpeg')]} />
    </FormSection>
  )
}
